import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtHora, fmtFechaHora, hoyISO, fechaISOhaceDias } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FilePdf, FileXls, FileCsv, ChartLineUp } from "@phosphor-icons/react";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_reportes_seen";

const TOUR_STEPS = [
  { target: "[data-tour='rep-filtros']", title: "Define el periodo", content: "Elige el rango de fechas y, opcionalmente, un empleado o centro de trabajo específico para acotar el reporte." },
  { target: "[data-tour='rep-vacio']", title: "Genera y exporta", content: "Al generar el reporte podrás descargarlo en CSV, Excel o PDF, listo para auditorías o trámites ante la STPS." },
];

export default function Reportes() {
  const [tipoReporte, setTipoReporte] = useState("jornadas"); // "jornadas" | "prenomina"
  const [empleados, setEmpleados] = useState([]);
  const [centros, setCentros] = useState([]);
  const [filters, setFilters] = useState({
    empleado_id: "todos",
    centro_id: "todos",
    fecha_desde: fechaISOhaceDias(30),
    fecha_hasta: hoyISO(),
  });
  const [data, setData] = useState(null);
  const [dataPre, setDataPre] = useState(null);
  const [loading, setLoading] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  useEffect(() => {
    Promise.all([api.get("/empleados"), api.get("/centros")]).then(([e, c]) => {
      setEmpleados(e.data); setCentros(c.data);
    });
  }, []);

  useEffect(() => {
    setData(null);
    setDataPre(null);
  }, [tipoReporte]);

  const params = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "todos") p.set(k, v);
    });
    return p;
  };

  const generar = async () => {
    setLoading(true);
    try {
      if (tipoReporte === "prenomina") {
        const { data: d } = await api.get("/reportes/prenomina?" + params().toString());
        setDataPre(d);
      } else {
        const { data: d } = await api.get("/reportes/jornadas?" + params().toString());
        setData(d);
      }
    } finally { setLoading(false); }
  };

  const download = async (kind) => {
    const path = tipoReporte === "prenomina" ? "/reportes/prenomina/export.xlsx" : `/reportes/export.${kind}`;
    const { data: blob } = await api.get(`${path}?${params().toString()}`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = tipoReporte === "prenomina" ? "prenomina.xlsx" : `jornadas.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10" data-testid="reportes-page">
      <PageHeader
        kicker="Cumplimiento"
        title="Reportes"
        subtitle="Concentrado de jornadas, horas ordinarias y extraordinarias por periodo. Exportable en PDF, Excel y CSV."
        actions={<TourHelpButton onClick={startTour} />}
      />

      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="flex gap-2 mb-4">
        <Button
          variant={tipoReporte === "jornadas" ? "default" : "outline"}
          className={tipoReporte === "jornadas" ? "bg-brand hover:bg-brand-hover h-9" : "h-9"}
          onClick={() => setTipoReporte("jornadas")}
          data-testid="rep-tab-jornadas"
        >
          Jornadas
        </Button>
        <Button
          variant={tipoReporte === "prenomina" ? "default" : "outline"}
          className={tipoReporte === "prenomina" ? "bg-brand hover:bg-brand-hover h-9" : "h-9"}
          onClick={() => setTipoReporte("prenomina")}
          data-testid="rep-tab-prenomina"
        >
          Prenómina
        </Button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-5 mb-6" data-tour="rep-filtros">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="label-uppercase">Desde</Label>
            <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })} className="h-9" />
          </div>
          <div>
            <Label className="label-uppercase">Hasta</Label>
            <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })} className="h-9" />
          </div>
          <div>
            <Label className="label-uppercase">Empleado</Label>
            <Select value={filters.empleado_id} onValueChange={(v) => setFilters({ ...filters, empleado_id: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase">Centro</Label>
            <Select value={filters.centro_id} onValueChange={(v) => setFilters({ ...filters, centro_id: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generar} className="bg-brand hover:bg-brand-hover h-9" data-testid="rep-generar-btn">
            {loading ? "Generando…" : "Generar reporte"}
          </Button>
        </div>
        {(data || dataPre) && (
          <div className="mt-4 flex gap-2 flex-wrap">
            {tipoReporte === "jornadas" && (
              <>
                <Button onClick={() => download("csv")} variant="outline" data-testid="rep-csv-btn"><FileCsv size={16} weight="bold" className="mr-2" /> CSV</Button>
                <Button onClick={() => download("xlsx")} variant="outline" data-testid="rep-xlsx-btn"><FileXls size={16} weight="bold" className="mr-2" /> Excel</Button>
                <Button onClick={() => download("pdf")} variant="outline" data-testid="rep-pdf-btn"><FilePdf size={16} weight="bold" className="mr-2" /> PDF</Button>
              </>
            )}
            {tipoReporte === "prenomina" && (
              <Button onClick={() => download("xlsx")} variant="outline" data-testid="rep-pre-xlsx-btn"><FileXls size={16} weight="bold" className="mr-2" /> Excel</Button>
            )}
          </div>
        )}
      </div>

      {!data && !dataPre && (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-md" data-tour="rep-vacio">
          <ChartLineUp size={42} weight="duotone" className="mx-auto text-slate-400 mb-3" />
          <p className="font-display font-bold text-slate-700">Selecciona filtros y genera tu reporte</p>
          <p className="text-sm text-slate-500 mt-1">
            {tipoReporte === "prenomina"
              ? "Faltas, incidencias y horas por trabajador para el periodo, listo para tu sistema de nómina."
              : "Los reportes incluyen empresa, RFC, periodo, horarios y emisor."}
          </p>
        </div>
      )}

      {tipoReporte === "jornadas" && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="kpi-card kpi-card-accent-slate">
              <div className="label-uppercase">Registros</div>
              <div className="font-display text-3xl font-black mt-2">{data.totales.registros}</div>
            </div>
            <div className="kpi-card kpi-card-accent-emerald">
              <div className="label-uppercase">H. Ordinarias</div>
              <div className="font-display text-3xl font-black mt-2">{data.totales.horas_ordinarias}</div>
            </div>
            <div className="kpi-card kpi-card-accent-emerald">
              <div className="label-uppercase">H. Extra</div>
              <div className="font-display text-3xl font-black mt-2 text-emerald-700">+{data.totales.horas_extra}</div>
            </div>
            <div className="kpi-card kpi-card-accent-amber">
              <div className="label-uppercase">Min. Retardo</div>
              <div className="font-display text-3xl font-black mt-2 text-amber-700">{data.totales.minutos_retardo}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>N° Emp</th>
                  <th>Nombre</th>
                  <th>RFC</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>H. Ord</th>
                  <th>H. Ext</th>
                  <th>Estatus</th>
                </tr>
              </thead>
              <tbody>
                {data.registros.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-xs">{r.fecha}</td>
                    <td className="font-mono text-xs">{r.numero_empleado}</td>
                    <td className="font-semibold">{r.nombre}</td>
                    <td className="font-mono text-xs">{r.rfc}</td>
                    <td className="font-mono">{fmtHora(r.hora_entrada, r.zona_horaria)}</td>
                    <td className="font-mono">{fmtHora(r.hora_salida, r.zona_horaria)}</td>
                    <td className="font-mono font-bold">{r.horas_ordinarias}</td>
                    <td className="font-mono font-bold text-emerald-700">{r.horas_extra > 0 ? `+${r.horas_extra}` : "0"}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">{r.estatus}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Emitido por: <strong>{data.emitido_por}</strong> · Fecha emisión: {fmtFechaHora(data.fecha_emision)} · Horas en la zona de cada centro
          </div>
        </>
      )}

      {tipoReporte === "prenomina" && dataPre && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="kpi-card kpi-card-accent-slate">
              <div className="label-uppercase">Trabajadores</div>
              <div className="font-display text-3xl font-black mt-2">{dataPre.totales.empleados}</div>
            </div>
            <div className="kpi-card kpi-card-accent-red">
              <div className="label-uppercase">Faltas Injustificadas</div>
              <div className="font-display text-3xl font-black mt-2 text-red-700">{dataPre.totales.faltas}</div>
            </div>
            <div className="kpi-card kpi-card-accent-emerald">
              <div className="label-uppercase">H. Ordinarias</div>
              <div className="font-display text-3xl font-black mt-2">{dataPre.totales.horas_ordinarias}</div>
            </div>
            <div className="kpi-card kpi-card-accent-amber">
              <div className="label-uppercase">Registros Pendientes</div>
              <div className="font-display text-3xl font-black mt-2 text-amber-700">{dataPre.totales.registros_pendientes}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N° Emp</th>
                  <th>Nombre</th>
                  <th>Centro</th>
                  <th>Días Lab.</th>
                  <th>Trabajados</th>
                  <th>Festivos</th>
                  <th>Faltas</th>
                  <th>Vac.</th>
                  <th>Incap.</th>
                  <th>Permiso c/g</th>
                  <th>Permiso s/g</th>
                  <th>H. Ord</th>
                  <th>H. Ext</th>
                  <th>Retardo (min)</th>
                  <th>Pendientes</th>
                </tr>
              </thead>
              <tbody>
                {dataPre.filas.map((f) => (
                  <tr key={f.empleado_id}>
                    <td className="font-mono text-xs">{f.numero_empleado}</td>
                    <td className="font-semibold">
                      {f.nombre}
                      {f.sin_horario_asignado && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">sin horario</span>
                      )}
                    </td>
                    <td className="text-xs">{f.centro}</td>
                    <td className="font-mono">{f.dias_laborables}</td>
                    <td className="font-mono">{f.dias_trabajados}</td>
                    <td className="font-mono">{f.dias_festivo}</td>
                    <td className="font-mono font-bold text-red-700">{f.faltas || ""}</td>
                    <td className="font-mono">{f.vacaciones || ""}</td>
                    <td className="font-mono">{f.incapacidad || ""}</td>
                    <td className="font-mono">{f.permiso_goce || ""}</td>
                    <td className="font-mono">{f.permiso_sin_goce || ""}</td>
                    <td className="font-mono font-bold">{f.horas_ordinarias}</td>
                    <td className="font-mono font-bold text-emerald-700">{f.horas_extra > 0 ? `+${f.horas_extra}` : "0"}</td>
                    <td className="font-mono">{f.minutos_retardo || ""}</td>
                    <td className="font-mono">
                      {f.registros_pendientes > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">{f.registros_pendientes}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Emitido por: <strong>{dataPre.emitido_por}</strong> · Fecha emisión: {fmtFechaHora(dataPre.fecha_emision)} ·
            Periodo: {dataPre.periodo.desde} a {dataPre.periodo.hasta}
          </div>
        </>
      )}
    </div>
  );
}
